import useTranslation from '../useTranslation';
import { useEffect, useState } from 'react';

import useGridRowSelection from 'hooks/useGridRowSelection';
import useGroupedGridRows from 'hooks/useGroupedGridRows';

import type { Identifiable, Props, UseData } from './types';

const useData = <T extends Identifiable>({
    getGroupKey,
    getSummaryText,
    isLoading = false,
    onNext,
    rows
}: Props<T>): UseData<T> => {
    const { NO_ITEMS, NO_MORE_ITEMS } = useTranslation();

    const { groups, isGroupOpen, toggleGroup } = useGroupedGridRows({
        getGroupKey,
        getSummaryText,
        rows
    });

    const {
        isAllIndeterminate,
        isAllSelected,
        isGroupIndeterminate,
        isGroupSelected,
        isRowSelected,
        selectedIds,
        toggleAll,
        toggleGroup: toggleGroupSelection,
        toggleRow
    } = useGridRowSelection(rows, { getGroupKey });

    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = rows.length === 0 ? (isLoading ? '' : NO_ITEMS) : NO_MORE_ITEMS;

    return {
        endingMessage,
        fetchNext,
        groups,
        hasNextPage,
        isAllIndeterminate,
        isAllSelected,
        isGroupIndeterminate,
        isGroupOpen,
        isGroupSelected,
        isRowSelected,
        selectedIds,
        toggleAll,
        toggleGroup,
        toggleGroupSelection,
        toggleRow
    };
};

export default useData;
